import {AfterViewChecked, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import {filter, map, switchMap, take, tap} from 'rxjs/operators';
import {IAngularMyDpOptions, IMyDate, IMyStyles} from 'angular-mydatepicker';
import {
  phoneNumberValidator,
} from '@unleashed/common/validators';
import { BusyService } from '@unleashed/services/common';
import {
  BookingConfiguration,
  Booking,
  Package,
  Attraction,
  HangoutMinimumGrouped, Timeslot, PreferredTime, Hangout, AttendeeRestrictions, ResourceTypeModifier, Item, AddOns, NavigationSteps
} from '@unleashed/models/booking';
import { AuthorizationService } from '@unleashed/services/account';
import { BookingSessionService } from '@unleashed/services/booking';
import { Park } from '@unleashed/models/park';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import {faCalendarAlt, faCheck, faInfoCircle} from '@fortawesome/free-solid-svg-icons';
import {Modal} from 'bootstrap';
import {AddOnApiService, AttendeeApiService, HangoutApiService, PackageApiService, TimeslotsApiService} from '@unleashed/api/booking';
import {PhoneNumberPipe} from '@unleashed/common/pipes';
import {APP_OPTIONS, AppOptions, BRAND_OPTIONS, BrandOptions} from '@unleashed/common/config';
import {TimeslotsDateComponent} from '@unleashed/booking/components';

@Component({
  selector: 'ua-wizard-first-step',
  templateUrl: './wizard-first-step.component.html',
  styleUrls: ['./wizard-first-step.component.scss']
})
export class WizardFirstStepComponent implements OnInit, OnDestroy, AfterViewChecked {
  bookingForm: FormGroup;
  park?: Park | null;
  configuration?: BookingConfiguration;
  formDirty = false;
  booking?: Booking;
  error?: number;
  isParentGuardianFieldsNotRequired?: boolean;
  subscriptions = new Subscription();

  booking$ = new BehaviorSubject<Booking | null>(null);
  updateBooking$ = new BehaviorSubject<Booking | null>(null);

  // Packages
  infoUpgradeModal?: Modal;
  packages: Package[] = [];
  upgradePackage?: Package;
  packagesLoaded = false;
  packageSelected = false;
  selectionChanged = false;
  infoPackage?: Package;
  upgradeAttractions: Attraction[] = [];
  availableAttractions: Attraction[] = [];
  showAsUpgrade = false;

  public readonly faInfoCircle = faInfoCircle;
  public readonly faCalendarAlt = faCalendarAlt;
  public readonly faCheck = faCheck;

  // Packages
  stylesData: IMyStyles = {
    selector: 'dp1',
    styles: `.dp1 .myDpPrevMonth,
             .dp1 .myDpNextMonth {
                color: #333;
             }
             .dp1 .myDpDisabled {
                color: #777;
                background: transparent;
                text-decoration: line-through;
             }`
  };
  today = this.myDateFromDate(new Date());
  preferredDateOptions: IAngularMyDpOptions = {
    dateRange: false,
    dateFormat: 'mm/dd/yyyy',
    firstDayOfWeek: 'su',
    disableUntil: this.today,
    sunHighlight: false,
    stylesData: this.stylesData
  };

  // Timeslots
  @ViewChild(TimeslotsDateComponent) timeslotsComponent!: TimeslotsDateComponent;
  timeslotSelected = false;
  timeslots: Timeslot[] = [];
  slotsLoaded = false;
  preferredTimes: PreferredTime[] = [];
  hangoutsGrouped: HangoutMinimumGrouped = {};
  minPrice = 0;
  amenities: string[] = [];
  amenitiesModalTimeSlots?: Modal;
  timeslotSelectionChanged = false;

  // Hangouts
  amenitiesModalHangouts?: Modal;
  hangoutsSelectionChanged = false;
  hangoutSelected = false;
  hangouts: Hangout[] = [];
  hangoutsLoaded = false;

  // Attendees
  participantCount = 0;
  adultParticipantCount = 0;
  attendeeRestrictions?: AttendeeRestrictions;
  phoneNumber?: string;
  attendeesText: string;
  adultParticipantPrice = 0;
  subtotal?: number;
  attendeesSelectionChanged = false;
  attendeesLoaded = false;
  phoneNumberPipe = new PhoneNumberPipe();
  brandId?: number;

  // Add-ons
  resourceTypeModifiers: ResourceTypeModifier[] = [];
  products: Item[] = [];
  selected = false;
  addOnSelectionChanged = false;
  addOnsLoaded = false;
  productServiceUrl: string;
  hasItemsToSelect?: boolean;
  addonQty = 0;
  addonItems: string[] = [];
  isAddOnValid = false;

  @ViewChild('addOnsSection') section!: ElementRef;
  private hasStartedObserving = false;
  private hasFired = false;

  // disable next button flag
  disableNext = false;

  constructor(
    private authService: AuthorizationService,
    private formBuilder: FormBuilder,
    private sessionService: BookingSessionService,
    private busyService: BusyService,
    private gtmService: GoogleTagManagerService,
    private packageApiService: PackageApiService,
    private timeslotsApiService: TimeslotsApiService,
    private hangoutApiService: HangoutApiService,
    private attendeeApiService: AttendeeApiService,
    private addOnApi: AddOnApiService,
    private cdr: ChangeDetectorRef,
    @Inject(BRAND_OPTIONS) private brandOptions: BrandOptions,
    @Inject(APP_OPTIONS) appOptions: AppOptions,
  ) {
    this.bookingForm =  new FormGroup({
      emailAddress: new FormControl(''),
      contactFirstName: new FormControl(''),
      contactLastName: new FormControl(''),
      phoneNumber: new FormControl(''),
      preferredDate: new FormControl(null),
      requiresHandicap: new FormControl(false)
    });

    this.attendeesText = brandOptions.attendeesText;
    this.brandId = brandOptions.brandId;
    this.productServiceUrl = appOptions.productApiUrl;
  }

  ngOnInit(): void {
    this.busyService.set('start', true);

    const element = document.getElementById('infoModal');
    if (element) {
      this.infoUpgradeModal = new Modal(element);
    }

    const elementAmenitiesModal = document.getElementById('amenitiesModalTimeSlots');
    if (elementAmenitiesModal) {
      this.amenitiesModalTimeSlots = new Modal(elementAmenitiesModal);
    }

    const elementAmenitiesModalHangouts = document.getElementById('amenitiesModalHangouts');
    if (elementAmenitiesModalHangouts) {
      this.amenitiesModalHangouts = new Modal(elementAmenitiesModalHangouts);
    }

    this.subscriptions.add(
      combineLatest([
        this.sessionService.getSession(),
        this.sessionService.getConfig()
      ]).pipe(
        tap(([session, config]) => {
          this.park = session.park;
          if (!this.park?.enrolledGLT && this.park?.phoneNumber) {
            this.phoneNumber = this.phoneNumberPipe.transform(this.park?.phoneNumber);
          } else {
            this.phoneNumber = this.phoneNumberPipe.transform(this.brandOptions.phoneNumber);
          }

          this.setConfig(config);
        }),
        switchMap(() => this.sessionService.getBooking()),
        take(1),
      ).subscribe(
        booking => {
          if (this.booking?.cookieId) {
            // If the booking is already set, don't override it
            return;
          }
          if (booking?.cookieId) {
            this.setBooking(booking);
            this.booking$.next(booking);
          } else {
            this.createCurrentBooking();
          }
          this.busyService.set('start', false);
        },
        err => {
          this.error = err.status;
          this.busyService.set('start', false);
        }
      )
    );

    this.subscriptions.add(
      this.booking$
        .pipe(
          filter(booking => !!booking && !!booking.cookieId && this.packages?.length === 0),
          switchMap((booking) => {
            if (booking) {
              return this.loadPackages(booking);
            } else {
              return of([]);
            }
          })
        ).subscribe(packages => {
          if (packages.length > 0) {
            this.upgradePackage = packages[0];
          }
          this.packages = packages;
          this.packagesLoaded = true;
        }
      )
    );

    this.subscriptions.add(
      this.updateBooking$
        .pipe(
          filter(booking => !!booking && !!booking.cookieId),
          tap(() => {
            this.disableNext = true; // trigger change detection again
            this.cdr.detectChanges();
          }),
          switchMap(booking => {
            if (booking) {
              const update: Booking = {
                ...(!!booking ? booking : {}),
                bookingItems: booking.bookingItems ?? [],
                parkId: this.park?.id ?? '',
                guestsOfHonor: [],
                preferredTimeId: 1,
                emailAddress: this.bookingForm.value.emailAddress,
                contactFirstName: this.bookingForm.value.contactFirstName,
                contactLastName: this.bookingForm.value.contactLastName,
                phoneNumber: this.bookingForm.value.phoneNumber,
                preferredDate: this.bookingForm.value.preferredDate.singleDate?.jsDate,
                requiresHandicap: this.bookingForm.value.requiresHandicap,
              };

              if (this.booking) {
                this.booking = update;
                this.booking$.next(this.booking);
              }

              return this.sessionService.upsertCart(update);
            } else {
              return of(null);
            }
          })
        ).subscribe(() => {
          this.disableNext = false;
          // trigger change detection again
          this.cdr.detectChanges();
        })
    );

    this.subscriptions.add(
      this.authService.getUser()
        .subscribe(user => {
            if (user.loggedIn) {
              this.bookingForm.controls.emailAddress.setValue(user.emailAddress ?? '');
              this.bookingForm.controls.contactFirstName.setValue(user.firstName ?? '');
              this.bookingForm.controls.contactLastName.setValue(user.lastName ?? '');
              this.bookingForm.controls.phoneNumber.setValue(user.phoneNumber ?? '');
            }
          },
          err => {
            this.error = err.status;
            this.busyService.set('start', false);
          })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (!this.hasStartedObserving && this.section) {
      this.hasStartedObserving = true;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !this.hasFired) {
            this.hasFired = true;
            (async () => {
              await this.onSectionEntered();
              observer.unobserve(this.section.nativeElement);
            })();
          }
        },
        { threshold: 0.1 }
      );

      observer.observe(this.section.nativeElement);
    }
  }

  async onSectionEntered(): Promise<void> {
    await this.pushAddOnsViewedGtmtags();
  }

  createCurrentBooking(): void {
    const prefDate = new Date();
    prefDate.setDate(prefDate.getDate() + (this.configuration?.minAdvanceDays ?? 1));
    const booking: Booking = {
      ...this.booking,
      parkId: this.park?.id ?? '',
      guestsOfHonor: [
        {
          firstName: '',
          lastName: '',
          dateOfBirth: new Date()
        }
      ],
      emailAddress: '',
      contactFirstName: '',
      contactLastName: '',
      phoneNumber: '',
      preferredDate: prefDate,
      preferredTimeId: 1,
      requiresHandicap: false,
      selectedDate : undefined,
      productLevelId : undefined,
      bookingItems: [],
    };

    this.authService.getUser()
      .pipe(switchMap(user => this.sessionService.upsertCart({
        ...booking,
        emailAddress: user?.emailAddress ?? '',
        contactFirstName: user?.firstName ?? '',
        contactLastName: user?.lastName ?? '',
        phoneNumber: user?.phoneNumber ?? '',
      })))
      .subscribe(
        newBooking => {
          this.setBooking(newBooking);
          this.booking$.next(newBooking);
        },
        err => {
          this.error = err.status;
          this.busyService.set('start-save', false);
        });
  }

  // Load data methods
  loadPackages(booking: Booking): Observable<Package[]> {
    return this.packageApiService.get(booking)
      .pipe(map(packages => {
        // Set helper props for binding
        packages.forEach(p => {
          p.selected = false;
          p.hasAttractions = p.attractions.length > 0;
          const heights = p.attractions.map(a => a.requiredHeight ?? 0);
          if (heights && heights.length > 0) {
            p.maxRequiredHeight = Math.max(...heights);
          }
        });
        // Set selected state
        if (booking.productLevelId) {
          const selected = packages.find(p =>
            p.productLevelId === booking.productLevelId);
          if (selected) {
            selected.selected = true;
            this.packageSelected = true;
            if (!booking.selectedStartTime && selected.minCombinedPrice !== booking.minSubtotal) {
              this.sessionService.setSubtotal(selected.minCombinedPrice);
              this.selectionChanged = true;
            }
            this.loadTimeslots(this.booking);
          }
        }
        return packages;
      }));
  }

  loadTimeslots(booking?: Booking): void {
    if (!booking) {
      return;
    }

    this.timeslotSelected = false;
    this.timeslotsApiService.get(booking)
      .subscribe(timeslots => {
        timeslots = timeslots.filter(t => t.isActive);
        const selectedIndex = timeslots.findIndex(t => t.startTime === booking.selectedStartTime && t.endTime === booking.selectedEndTime);
        for (let i = 0; i < timeslots.length; i++) {
          if (selectedIndex === i) {
            this.timeslotSelected = true;
            timeslots[i].selected = true;
            timeslots[i].displayPrice = 0;
          } else {
            timeslots[i].displayPrice = selectedIndex >= 0
              ? timeslots[i].price - timeslots[selectedIndex].price
              : timeslots[i].price - (booking.minSubtotal ?? 0);
          }
        }
        this.timeslots = timeslots;
        this.slotsLoaded = true;

        const selected = timeslots.find(t => t.startTime === booking.selectedStartTime && t.endTime === booking.selectedEndTime);
        if (selected) {
          this.scrollToSelectedOrPreferred(selectedIndex);
          this.loadHangouts(booking);
        } else {
          this.scrollToSelectedOrPreferred();
        }
      }, err => this.error = err.status);

    this.hangoutApiService.getHangoutsMinimum(booking).subscribe(hangouts => {
      const tempHangouts: HangoutMinimumGrouped = {};
      // get the lowest price hangout for each start hour
      for (const hangout of hangouts) {
        if (hangout?.startHour !== undefined) {
          if (tempHangouts[hangout.startHour] === undefined || tempHangouts[hangout.startHour].price > hangout.price) {
            tempHangouts[hangout.startHour] = hangout;
          }
        }
      }
      this.hangoutsGrouped = tempHangouts;
    });
  }

  loadHangouts(booking: Booking): void {
    this.hangoutSelected = false;
    this.hangoutApiService.getHangouts(booking)
      .subscribe(hangouts => {

        let selectedHangoutIndex = hangouts
          .findIndex(h => h.resourceTypeId === booking.selectedResourceTypeId
            && h.price === booking.selectedResourcePrice
            && h.available > 0);

        const minPrice = Math.min(...hangouts.map(h => h.price));

        if (selectedHangoutIndex === -1) {
          const available = hangouts.filter(h => h.available);
          if (available.length && available[available.length - 1].price === minPrice) {
            selectedHangoutIndex = hangouts.indexOf(available[available.length - 1]);
            this.hangoutsSelectionChanged = true;
          }
        }

        for (let i = 0; i < hangouts.length; i++) {
          if (i === selectedHangoutIndex) {
            hangouts[i].selected = true;
            hangouts[i].displayPrice = 0;
            this.hangoutSelected = true;
          } else {
            hangouts[i].displayPrice = selectedHangoutIndex >= 0
              ? hangouts[i].price - hangouts[selectedHangoutIndex].price
              : hangouts[i].price;
          }
        }
        this.hangoutsLoaded = true;
        this.hangouts = hangouts;

        if (selectedHangoutIndex !== -1) {
          this.hangoutsSelectionChanged = true;
          const selected = hangouts[selectedHangoutIndex];
          booking.selectedResourceTypeId = selected?.resourceTypeId;
          booking.selectedResourcePrice = selected?.price;

          if (booking.scheduleDetailId) {
            this.loadAttendeesRestrictions(booking);
          } else {
            this.confirmHangouts(booking);
          }
        }

        this.scrollToHangout(selectedHangoutIndex);
      }, err => this.error = err.status);
  }

  loadAttendeesRestrictions(booking: Booking, triggerSave = true): void {
    this.attendeeApiService.getAttendeeRestrictions(booking)
      .subscribe(attendeeRestrictions => {
        this.attendeeRestrictions = attendeeRestrictions;
        if (this.booking?.baseParticipantCount !== undefined && this.booking?.extraParticipantCount !== undefined) {
          this.participantCount = this.booking?.baseParticipantCount + this.booking?.extraParticipantCount;
        }
        else {
          this.participantCount = this.attendeeRestrictions.minQuantity;
        }
        this.adultParticipantCount = this.booking?.adultParticipantCount && this.attendeeRestrictions.adultParticipantPrice !== undefined
          ? this.booking?.adultParticipantCount
          : 0;
        this.adultParticipantPrice = this.attendeeRestrictions?.adultParticipantPrice ?? 0 > 0
          ? (this.attendeeRestrictions.adultParticipantPrice ?? 0)
          : ((this.attendeeRestrictions.participantPrice ?? 0) / 2);

        if (attendeeRestrictions.participantPrice == null) {
          this.attendeeRestrictions.maxQuantity = this.attendeeRestrictions.minQuantity;
        }
        this.attendeesLoaded = true;
        this.setBookingAttendees(triggerSave);
      }, err => this.error = err.status);
  }

  loadAddOns(booking: Booking): Observable<AddOns> {
    this.addOnsLoaded = false;
    return this.addOnApi.get(booking)
      .pipe(tap(addOns => {
        this.hasItemsToSelect = addOns.resourceTypeModifiers && addOns.resourceTypeModifiers.length > 0;
        addOns.products = addOns.products.filter(p => p.isActive);
        addOns.includedProducts = addOns.includedProducts.filter(p => p.isActive);
        addOns.products.forEach(p => {
          p.quantity = 0;
          p.imageUri = p.imageUri ? `${this.productServiceUrl}/products-service/products/${p.imageUri}` : `/assets/images/urbie.png`;
        });
        addOns.resourceTypeModifiers.forEach(r => {
          r.products = addOns.products.filter(p => p.productModifierTypeId === r.productModifierTypeId);
        });
        addOns.resourceTypeModifiers = addOns.resourceTypeModifiers.filter(r => r.products.length > 0);
        addOns.includedProducts.forEach(i => {
          const existsAsModifier =
            addOns.resourceTypeModifiers.findIndex(r => r.products.length === 1
              && (
                (r.products[0].parkProductId && r.products[0].parkProductId === i.parkProductId
                  && r.products[0].bundleScheduleId && r.products[0].bundleScheduleId === i.bundleScheduleId)
                || (
                  r.products[0].ticketId && r.products[0].ticketId === i.ticketId
                  && r.products[0].bundleScheduleId && r.products[0].bundleScheduleId === i.bundleScheduleId
                )));

          const product = {
            id: i.id,
            parkProductName: i.parkProductName,
            parkProductId: i.parkProductId,
            ticketId: i.ticketId,
            imageUri: '',
            quantity: 0,
            price: 0,
            productModifierTypeId: i.productModifierTypeId,
            included: true,
            bundleScheduleId: i.bundleScheduleId,
          };
          if (existsAsModifier >= 0) {
            addOns.resourceTypeModifiers[existsAsModifier].quantity += i.quantity;
            addOns.resourceTypeModifiers[existsAsModifier].relatedBookingItems.push({...product, quantity: i.quantity});
          } else {
            addOns.resourceTypeModifiers.push({
              productModifierTypeId: i.parkProductId ?? i.ticketId ?? 0, // yes, this was intentional
              name: i.parkProductName,
              quantity: i.quantity,
              products: [product],
              relatedBookingItems: [{
                ...product,
                quantity: i.quantity
              }]
            });
          }
        });

        this.resourceTypeModifiers = addOns.resourceTypeModifiers;
        this.isAddOnValid = !this.hasItemsToSelect;
        this.products = addOns.products.filter(p => p.isPartyAddon);
        this.booking?.bookingItems?.filter(i => !i.included)
          .forEach(i => {
            const product = this.products.find(p => p.parkProductId === i.parkProductId);
            if (product) {
              product.id = i.id;
              product.quantity = i.quantity;
            }
          });

        this.addOnsLoaded = true;

        // Track any previous adds items
        this.products.filter(p => p.quantity > 0).forEach(p => {
          this.addonItems.push(p.parkProductName);
          this.addonQty += p.quantity;
        });
      }));
  }

  setConfig(config: BookingConfiguration): void {
    this.configuration = config;
    const minBookingDate = new Date(this.today.year, this.today.month - 1, this.today.day + (config?.minAdvanceDays ?? 2) - 1);
    const maxAdvanceDate = new Date(this.today.year, this.today.month - 1, this.today.day + (config?.maxAdvanceDays ?? 60));
    this.preferredDateOptions.disableUntil = this.myDateFromDate(minBookingDate);
    this.preferredDateOptions.disableSince = this.myDateFromDate(maxAdvanceDate);
    this.busyService.set('start', false);
  }

  setBooking(booking: Booking): void {
    this.booking = booking;
    this.bookingForm = this.formBuilder.group({
    });

    this.bookingForm.addControl('emailAddress', this.formBuilder.control(booking.emailAddress ?? '', null));
    this.bookingForm.addControl('contactFirstName', this.formBuilder.control(booking.contactFirstName ?? '', null));
    this.bookingForm.addControl('contactLastName', this.formBuilder.control(booking.contactLastName ?? '', null));
    this.bookingForm.addControl('phoneNumber', this.formBuilder.control(booking.phoneNumber ?? '', phoneNumberValidator()));
    this.bookingForm.addControl('requiresHandicap', this.formBuilder.control(booking.requiresHandicap ?? false, null));
    this.bookingForm.addControl('preferredDate', this.formBuilder.control(booking.isDefault
      ? ''
      : {
        isRange: false,
        singleDate: {jsDate: new Date(booking.preferredDate)}
      }, Validators.required));
    // Listen for changes to the date
    this.bookingForm.get('preferredDate')?.valueChanges.subscribe(date => {
      if (date && date.singleDate) {
        if (this.booking) {
          this.booking.preferredDate = date.singleDate.jsDate;
          this.booking$.next(this.booking);
        }
        this.updateDate(date.singleDate.jsDate);
      }
    });
  }

  // Packages
  showModal(partyPackage: Package): void {
    this.infoPackage = partyPackage;
    this.upgradeAttractions = this.packages
      .filter(p => p.ordinal < partyPackage.ordinal)
      .map(p => p.attractions)
      .reduce((previous, current) => ([...previous, ...current]), []);
    this.availableAttractions = this.packages
      .filter(p => p.ordinal >= partyPackage.ordinal)
      .map(p => p.attractions)
      .reduce((previous, current) => ([...previous, ...current]), []);

    this.infoUpgradeModal?.show();
  }

  toggleSelection(index: number): void {
    this.selectionChanged = true;
    let subtotal = this.booking?.minSubtotal ?? 0;
    for (let i = 0; i < this.packages.length; i++) {

      if (i === index) {
        continue;
      }

      this.packages[i].selected = false;
    }

    this.packages[index].selected = !this.packages[index].selected;
    if (this.packages[index].selected && this.packages[index] !== this.packages[0]) {
      this.showAsUpgrade = true;
      this.showModal(this.packages[index]);
    }

    subtotal = this.packages[index].selected ? this.packages[index].minCombinedPrice : 0;
    this.sessionService.setSubtotal(subtotal);
    this.packageSelected = this.packages.find(p => p.selected) !== undefined;

    if (this.booking) {
      this.booking.productLevelId = this.packages[index].productLevelId;
      this.booking.minSubtotal = this.packageSelected ? this.packages[index].minCombinedPrice : 0;

      this.booking.selectedDate = undefined;
      this.booking.selectedPreferredTimeId = undefined;
      this.booking.selectedStartTime = undefined;
      this.booking.selectedEndTime = undefined;
      this.booking.scheduleDetailId = undefined;
      this.booking.baseParticipantCount = undefined;
      this.booking.extraParticipantCount = undefined;
      this.booking.adultParticipantCount = undefined;
      this.booking.timeBegan = undefined;
      this.booking.bookingItems = [];
      this.booking.baseBundleScheduleId = undefined;
      this.booking.selectedResourcePrice = undefined;
      this.booking.selectedResourceTypeId = undefined;
      this.booking.adultBundleScheduleId = undefined;
      this.booking.extraBundleScheduleId = undefined;

      this.booking$.next(this.booking);
      this.sessionService.upsertCart(this.booking).subscribe(b => {
        if (this.packageSelected && this.booking?.preferredDate) {
          this.updateDate(this.booking?.preferredDate);
        }
      });

      this.gtmService.pushTag({
        event: 'packageSelectClicked',
        selectedPackage: this.packageSelected ? this.packages[index]?.productLevelName : null,
      });

      this.clearPackageUnselection();
    }
  }

  closeModal(): void {
    this.infoUpgradeModal?.hide();
    this.showAsUpgrade = false;
  }

  upgrade(): void {
    this.toggleSelection(0);
    this.closeModal();
  }

  myDateFromDate(date: Date): IMyDate {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    };
  }

  // Times slots
  scrollToSelectedOrPreferred(index?: number): void {
    let elem: HTMLElement | null;
    if (index !== undefined) {
      if (index === -1) {
        const preferredTime = this.preferredTimes.find(t => t.id === this.booking?.preferredTimeId);
        index = this.timeslots.findIndex(ts => ts.startTime >= (preferredTime?.startHour ?? 0));
      }

      // Give Angular a moment to render the array
      setTimeout(() => {
        elem = document.getElementById('card-' + index);
        elem?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else {
      setTimeout(() => {
        elem = document.getElementById('divChooseATime');
        elem?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }

  updateDate(event: Date): void {
    if (!this.booking || !this.booking.preferredTimeId || !this.booking.productLevelId) {
      return;
    }

    this.timeslotSelectionChanged = true;

    if (this.booking) {
      this.booking.selectedDate = event;
      this.booking.selectedStartTime = undefined;
      this.booking.selectedEndTime = undefined;
      this.clearHangouts();
      this.booking$.next(this.booking);
      this.updateBooking$.next(this.booking);
    }

    this.sessionService.setSubtotal(this.booking.minSubtotal ?? 0);
    this.timeslotSelected = false;

    this.slotsLoaded = false;
    this.timeslots = [];
    this.timeslotsComponent.booking = this.booking;
    this.timeslotsComponent?.setDate({date: event, selected: true}, false);
    this.loadTimeslots(this.booking);
  }

  updateMinPrice(event: number): void {
    this.minPrice = event;
  }

  showAmenitiesModalTimeslots(hangout: Hangout): void {
    this.amenitiesModalTimeSlots?.show();
    this.amenities = hangout.amenities;
  }

  toggleTimeslotSelection(index: number): void {
    this.timeslotSelectionChanged = true;
    let subtotal = 0;
    this.timeslots[index].selected = !this.timeslots[index].selected;
    if (this.timeslots[index].selected) {
      subtotal = this.timeslots[index].price;
      this.timeslots[index].displayPrice = 0;
    } else if (this.booking) {
      subtotal = this.minPrice ?? 0;
      this.timeslots[index].displayPrice = this.timeslots[index].price - subtotal;
    }

    for (let i = 0; i < this.timeslots.length; i++) {
      if (i === index) {
        continue;
      }

      this.timeslots[i].selected = false;
      if (this.timeslots[index].selected) {
        this.timeslots[i].displayPrice = this.timeslots[i].price - this.timeslots[index].price;
      } else if (this.booking) {
        this.timeslots[i].displayPrice = this.timeslots[i].price - subtotal;
      }
    }

    this.sessionService.setSubtotal(subtotal);
    this.timeslotSelected = this.timeslots.find(s => s.selected) !== undefined;

    if (this.booking) {
      const selected = this.timeslots.find(s => s.selected);
      if (selected) {
        this.booking.selectedStartTime = selected?.startTime;
        this.booking.selectedEndTime = selected?.endTime;
        this.booking.selectedDate = this.booking.selectedDate || this.booking.preferredDate;
        this.booking.baseBundleScheduleId = selected?.baseBundleScheduleId;
        this.booking.extraBundleScheduleId = selected?.extraBundleScheduleId;
        this.booking.adultBundleScheduleId = selected?.adultBundleScheduleId;
        this.booking.timeBegan = new Date();
        this.booking.minSubtotal = selected?.price;
        this.booking.partyThemeId = selected.partyThemeId;

        this.booking.baseParticipantCount = undefined;
        this.booking.extraParticipantCount = undefined;
        this.booking.adultParticipantCount = undefined;
        this.booking.bookingItems = [];
        this.booking.selectedResourceTypeId = undefined;
        this.booking.selectedResourcePrice = undefined;
        this.booking.scheduleDetailId = undefined;
        this.booking$.next(this.booking);
        this.updateBooking$.next(this.booking);
        this.loadHangouts(this.booking);

        this.gtmService.pushTag({
          event: 'timeSelectClicked',
          selectedTime: `${selected?.startTime} - ${selected?.endTime}`
        });
      } else {
        this.booking.selectedPreferredTimeId = undefined;
        this.booking.selectedStartTime = undefined;
        this.booking.selectedEndTime = undefined;
        this.booking.scheduleDetailId = undefined;
        this.booking.baseParticipantCount = undefined;
        this.booking.extraParticipantCount = undefined;
        this.booking.adultParticipantCount = undefined;
        this.booking.timeBegan = undefined;
        this.booking.bookingItems = [];
        this.booking.baseBundleScheduleId = undefined;
        this.booking.selectedResourcePrice = undefined;
        this.booking.selectedResourceTypeId = undefined;
        this.booking.adultBundleScheduleId = undefined;
        this.booking.extraBundleScheduleId = undefined;
        this.booking.partyThemeId = undefined;

        this.clearHangouts();

        this.booking$.next(this.booking);
        this.updateBooking$.next(this.booking);
      }
    }
  }

  // Hangouts
  scrollToHangout(index?: number): void {
    let elem: HTMLElement | null;
    if (index === -1) {
      index = 0;
    }

    // Give Angular a moment to render the array
    setTimeout(() => {
      elem = document.getElementById('divHangouts');
      elem?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }

  toggleHangoutsSelection(index: number): void {
    if (!this.booking) {
      return;
    }

    this.hangoutsSelectionChanged = true;
    if (this.booking) {
      this.booking.bookingItems = [];
      this.booking.participantSubtotal = undefined;
      this.booking.selectedResourcePrice = undefined;
      this.booking$.next(this.booking);
    }

    let subtotal = this.sessionService.getSubtotal(this.booking);
    for (let i = 0; i < this.hangouts.length; i++) {
      if (i === index) {
        continue;
      }
      this.hangouts[i].selected = false;
      if (!this.hangouts[index].selected) {
        this.hangouts[i].displayPrice = this.hangouts[i].price - this.hangouts[index].price;
      } else {
        this.hangouts[i].displayPrice = this.hangouts[i].price;
      }
    }

    this.hangouts[index].selected = !this.hangouts[index].selected;
    if (this.hangouts[index].selected) {
      subtotal += this.hangouts[index].price;
      this.hangouts[index].displayPrice = 0;
    } else {
      this.hangouts[index].displayPrice = this.hangouts[index].price;
    }

    if (this.booking) {
      this.sessionService.setSubtotal(subtotal);

      const selected = this.hangouts.find(t => t.selected);
      this.booking.selectedResourceTypeId = selected?.resourceTypeId;
      this.booking.selectedResourcePrice = selected?.price;
      this.booking.baseParticipantCount = undefined;
      this.booking.extraParticipantCount = undefined;
      this.booking.adultParticipantCount = undefined;
      this.booking.bookingItems = [];

      this.booking$.next(this.booking);
      if (selected) {
        this.hangoutSelected = true;
        this.confirmHangouts(this.booking);
        this.gtmService.pushTag({
          event: 'hangoutSelectClicked',
          selectedHangout:  selected?.name
        });
      } else {
        this.hangoutSelected = false;
        this.updateBooking$.next(this.booking);

        this.loadAttendeesRestrictions(this.booking);
      }
    }
  }

  showAmenitiesModalHangouts(hangout: Hangout): void {
    this.amenitiesModalHangouts?.show();
    this.amenities = hangout.amenities;
  }

  confirmHangouts(booking: Booking, allowReload = true): void {
    this.sessionService.confirmHangout(booking)
      .subscribe(
        b => {
          if (!b.scheduleDetailId && booking && allowReload) {
            this.hangoutsLoaded = false;
            this.hangoutSelected = false;
            this.loadHangouts(booking);
            return;
          }
          if (this.booking) {
            this.booking.scheduleDetailId = b.scheduleDetailId;
            this.loadAttendeesRestrictions(this.booking);
          }
        },
        err => {
          this.error = err.status;
          this.busyService.set('hangouts', false);
        });
  }

  // Participants
  async addParticipant(): Promise<void> {
    if (this.booking && this.attendeeRestrictions?.participantPrice != null) {
      this.attendeesSelectionChanged = true;
      this.participantCount++;

      this.setBookingAttendees();

      await this.pushParticipantGtmTags();
    }
  }

  async addAdultParticipant(): Promise<void> {
    if (this.booking) {
      this.attendeesSelectionChanged = true;
      this.adultParticipantCount++;

      this.setBookingAttendees();

      await this.pushParticipantGtmTags();
    }
  }

  async subtractParticipant(): Promise<void> {
    if (this.booking && this.attendeeRestrictions?.participantPrice != null) {
      this.attendeesSelectionChanged = true;
      this.participantCount--;

      this.setBookingAttendees();

      await this.pushParticipantGtmTags();
    }
  }

  async subtractAdultParticipant(): Promise<void> {
    if (this.booking) {
      this.attendeesSelectionChanged = true;
      this.adultParticipantCount--;

      this.setBookingAttendees();

      await this.pushParticipantGtmTags();
    }
  }

  setBookingAttendees(triggerSave = true): void {
    if (this.booking) {
      this.booking.baseParticipantCount = this.attendeeRestrictions?.minQuantity ?? 0;
      this.booking.extraParticipantCount = this.participantCount - (this.attendeeRestrictions?.minQuantity ?? 0);
      this.booking.adultParticipantCount = this.adultParticipantCount;

      this.booking$.next(this.booking);

      if (triggerSave) {
        this.sessionService.upsertCart(this.booking)
          .pipe(switchMap(_ => this.loadAddOns(this.booking ?? {} as Booking)))
          .subscribe();
      } else {
        this.loadAddOns(this.booking).subscribe();
      }
    }
  }

  // Add-ons
  updateAddOnIsValid(valid: boolean): void {
    this.isAddOnValid = valid;
  }

  updateBookingItems(event: Item[]): void {
    this.addOnSelectionChanged = true;
    if (this.booking) {
      const currentBookingItems = [...this.booking.bookingItems];
      const nonIncludedExisting = currentBookingItems.filter(bi => !bi.included);
      this.booking.bookingItems = [
        ...event.filter(bi => bi.included),
        ...nonIncludedExisting
      ];
      this.booking$.next(this.booking);
      this.updateBooking$.next(this.booking);
    }
  }

  async removeAddOn(index: number): Promise<void> {
    if (this.products[index]?.price == null || !this.booking) {
      return;
    }

    this.addonQty--;
    this.addonItems = this.addonItems.filter(item => item !== this.products[index].parkProductName);

    this.addOnSelectionChanged = true;
    this.products[index].quantity--;

    this.updateAddOns();

    await this.pushAddOnsGtmTags();
  }

  async addAddOn(index: number): Promise<void> {
    if (this.products[index]?.price == null || !this.booking) {
      return;
    }

    this.addonQty++;
    if (!this.addonItems.includes(this.products[index].parkProductName)) {
      this.addonItems.push(this.products[index].parkProductName);
    }

    this.selectionChanged = true;
    this.products[index].quantity++;

    this.updateAddOns();

    await this.pushAddOnsGtmTags();
  }

  updateAddOns(update: boolean = true): void {
    if (this.booking) {
      const currentBookingItems = [...this.booking.bookingItems];
      this.booking.bookingItems = [];
      this.products.forEach(p => {
          if (p.quantity > 0) {
            const existing = this.booking?.bookingItems.find(bp => bp.parkProductId === p.parkProductId);
            if (existing) {
              existing.quantity = p.quantity;
            } else {
              this.booking?.bookingItems?.push({
                included: false,
                parkProductName: p.parkProductName,
                quantity: p.quantity,
                productModifierTypeId: p.productModifierTypeId,
                parkProductId: p.parkProductId,
                price: p.price,
                bundleScheduleId: p.bundleScheduleId,
              });
            }
          } else {
            if (this.booking) {
              this.booking.bookingItems = this.booking.bookingItems.filter(item => item.parkProductId !== p.parkProductId) ?? [];
            }
          }
        }
      );

      const selectableIncluded = this.resourceTypeModifiers.filter(r => r.products.length > 1);
      selectableIncluded.forEach(r => {
        const parkProducts: number[] = [];
        r.products.forEach(pp => parkProducts.push(pp.parkProductId ?? 0));
        const existing = currentBookingItems.filter(bp => parkProducts.includes(bp.parkProductId ?? 0) && bp.included);
        if (existing?.length > 0) {
          existing.forEach(item => this.booking?.bookingItems?.push(item));
        }
      });

      const nonSelectableIncluded = this.resourceTypeModifiers.filter(r => r.products.length === 1);
      this.selectionChanged = true;
      nonSelectableIncluded.forEach(r => {
        const existing = this.booking?.bookingItems.find(bp =>
          bp.parkProductId === r.products[0].parkProductId
          && bp.bundleScheduleId === r.products[0].bundleScheduleId
          && bp.included === true);
        if (existing) {
          existing.quantity = existing.quantity + r.quantity;
        } else {
          this.booking?.bookingItems?.push({
            id: r.products[0].id,
            included: true,
            parkProductName: r.products[0].parkProductName,
            quantity: r.quantity,
            productModifierTypeId: r.productModifierTypeId,
            parkProductId: r.products[0].parkProductId,
            ticketId: r.products[0].ticketId,
            price: 0,
            bundleScheduleId: r.products[0].bundleScheduleId,
            scheduleDetailId: r.scheduleDetailId
          });
        }
      });
      if (update) {
        this.booking$.next(this.booking);
        this.updateBooking$.next(this.booking);
      }
    }
  }

  // Misc
  async goToReview(): Promise<void>  {
    if (this.booking) {
      this.updateAddOns(false);
      await this.pushGtmTags();
      if (this.booking.scheduleDetailId) {
        this.sessionService.upsertCart({...this.booking, step: 6})
          .subscribe(() => this.sessionService.navigateToStep(NavigationSteps.Review));
      } else {
        this.sessionService.confirmHangout(this.booking)
          .pipe(
            switchMap(booking => {
              if (this.booking) {
                return this.sessionService.upsertCart({
                  ...this.booking,
                  step: 6,
                  scheduleDetailId: booking.scheduleDetailId
                });
              } else {
                return of(booking);
              }
            })
          )
        .subscribe(() => this.sessionService.navigateToStep(NavigationSteps.Review));
      }
    }
  }

  stepChange(step: number): void {
    switch (step) {
      case 2:
        if (!this.disableNext && this.addOnsLoaded && this.isAddOnValid && this.hangoutSelected) {
          this.goToReview();
        }
        break;
      case 3:
        break;
    }
  }

  private async pushGtmTags(): Promise<void> {
    await this.gtmService.pushTag({
      event: 'startFormSubmitted',
      preferredPartyDate:  this.bookingForm.value.preferredDate.singleDate?.jsDate,
      preferredPartyTime:  this.bookingForm.value.preferredTime,
      handicapAccommodations: this.bookingForm.value.requiresHandicap ? 'yes' : 'no'
    });
  }

  private async pushAddOnsGtmTags(): Promise<void> {
    await this.gtmService.pushTag({
      event: 'addonsSelectClicked',
      addonItems: this.addonItems.join(', '),
      addonQty: this.addonQty.toString()
    });
  }

  private async pushAddOnsViewedGtmtags(): Promise<void> {
    await this.gtmService.pushTag({
      event: 'addonsViewed',
    });
  }

  private async pushParticipantGtmTags(): Promise<void>  {
    await this.gtmService.pushTag({
      event: 'attendeesSelectClicked',
      numberOfKids: this.attendeeRestrictions?.minQuantity ?? 0,
      numberOfAdults: this.adultParticipantCount,
      numberOfAdditional: this.participantCount - (this.attendeeRestrictions?.minQuantity ?? 0)
    });
  }

  private clearPackageUnselection(): void {
    this.clearTimeslots();
  }

  private clearTimeslots(): void {
    this.timeslotSelected = false;
    this.timeslots = [];
    this.slotsLoaded = false;
    this.preferredTimes = [];
    this.hangoutsGrouped = {};
    this.minPrice = 0;
    this.amenities = [];
    this.timeslotSelectionChanged = false;

    this.clearHangouts();
  }

  private clearHangouts(): void {
    this.hangoutsSelectionChanged = false;
    this.hangoutSelected = false;
    this.hangouts = [];
    this.hangoutsLoaded = false;

    this.clearParticipants();
    this.clearAddOns();
  }

  private clearParticipants(): void {
    this.participantCount = 0;
    this.adultParticipantCount = 0;
    this.adultParticipantPrice = 0;
    this.attendeesSelectionChanged = false;
    this.attendeesLoaded = false;
  }

  private clearAddOns(): void {
    this.resourceTypeModifiers = [];
    this.products = [];
    this.addOnSelectionChanged = false;
    this.addOnsLoaded = false;
    this.addonQty = 0;
    this.addonItems = [];
    this.isAddOnValid = false;
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }
}
